import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, User, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
];

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    project_description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTimeSelect = (value) => {
    setFormData({ ...formData, preferred_time: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/consultations`, formData);
      toast.success("Consultation request submitted successfully!");
      setIsSubmitted(true);
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        id="booking"
        className="py-24 md:py-32 relative"
        data-testid="booking-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="section-title mb-4">Request Submitted!</h2>
            <p className="section-subtitle mb-8">
              Thank you for your interest in GuruTech AI. Our team will review
              your request and contact you within 24 hours to schedule your
              consultation.
            </p>
            <Button
              onClick={() => setIsSubmitted(false)}
              className="btn-secondary"
              data-testid="booking-another-btn"
            >
              Book Another Consultation
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="booking"
      className="py-24 md:py-32 relative"
      data-testid="booking-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-jetbrains text-xs tracking-widest uppercase text-neon-cyan mb-4">
              Book a Consultation
            </p>
            <h2 className="section-title mb-6">
              Discuss Your Idea
              <br />
              <span className="text-gradient">With AI Experts</span>
            </h2>
            <p className="section-subtitle mb-8">
              Schedule a free consultation with our founder or senior AI
              engineers. We'll discuss your challenges, explore AI opportunities,
              and provide actionable recommendations.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-electric-violet/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-electric-violet" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">
                    Meet the Founder
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Get direct access to our founder Suyash Saxena for strategic
                    discussions.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-neon-cyan" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">
                    Flexible Scheduling
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Choose a time that works best for you. We accommodate all
                    time zones.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white/60" />
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">
                    30-Minute Session
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Focused discussion on your AI needs with actionable next
                    steps.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="glass-card p-8 space-y-6"
              data-testid="booking-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="input-field"
                    data-testid="booking-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="input-field"
                    data-testid="booking-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-white">
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company"
                    className="input-field"
                    data-testid="booking-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 234 567 890"
                    className="input-field"
                    data-testid="booking-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="preferred_date" className="text-white">
                    Preferred Date
                  </Label>
                  <Input
                    id="preferred_date"
                    name="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="booking-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Preferred Time</Label>
                  <Select onValueChange={handleTimeSelect}>
                    <SelectTrigger
                      className="input-field"
                      data-testid="booking-time"
                    >
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project_description" className="text-white">
                  Tell us about your project *
                </Label>
                <Textarea
                  id="project_description"
                  name="project_description"
                  value={formData.project_description}
                  onChange={handleChange}
                  required
                  placeholder="Describe your AI needs, challenges, or ideas..."
                  rows={4}
                  className="input-field resize-none"
                  data-testid="booking-description"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
                data-testid="booking-submit"
              >
                {isSubmitting ? "Submitting..." : "Book Free Consultation"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
